import type { DetectedIssueAttributes, SessionAttendeeAttributes } from '@/server/models'

import DetectedIssueQ from '@/queries/DetectedIssueQ'
import SessionBreakoutQ from '@/queries/SessionBreakoutQ'
import { SQL, type SQLFragment } from '@/client/query'

import CourseQ from './CourseQ'
import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import { SessionAttendeeRole } from './constants'
import SessionQ from './SessionQ'
import UserQ from './UserQ'

const SessionAttendeeQ = {
  ...makeQueryModelV2<SessionAttendeeAttributes>('session_attendee')([
    'id',
    'session_id',
    'session_breakout_id',
    'user_id',
    'joined_at',
    'duration_seconds',
    'volunteer_activity_id',
    'role',
    'speaking_duration_seconds',
    'count_chats',
    'created_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    Session: { query: SessionQ, foreignKey: SessionQ.id, sourceKey: SessionAttendeeQ.session_id },
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: SessionAttendeeQ.user_id,
      // Include soft-deleted users by default, since we keep SessionAttendee
      // rows even after associated user is deleted.
      paranoid: false,
    },
    SessionBreakout: {
      query: SessionBreakoutQ,
      foreignKey: SessionBreakoutQ.id,
      sourceKey: SessionAttendeeQ.session_breakout_id,
    },
  }),

  // NOTE: By default we do not include 'tutor' roles in query, since this is the most common use case.
  where: (
    {
      id,
      sessionId,
      sessionBreakoutId = false,
      userId,
      roles = [SessionAttendeeRole.Learner, SessionAttendeeRole.OnboardingBuddy],
      include = [],
      groupBy = null,
      order = null,
      limit = null,
      paranoid = true,
    }: {
      id?: number | null | SQLFragment<number>
      sessionId?: number | null | SQLFragment<number>
      sessionBreakoutId?: number | boolean | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      roles?: SessionAttendeeRole[]
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SessionAttendeeAttributes> => SQL`
    FROM ${SessionAttendeeQ}
    ${includeAssociationsToSql(SessionAttendeeQ, include)}
    WHERE
        ${id != null ? SQL`${SessionAttendeeQ.id} = ${id} AND` : SQL``}
        ${sessionId != null ? SQL`${SessionAttendeeQ.session_id} = ${sessionId} AND` : SQL``}
        ${
          typeof sessionBreakoutId === 'boolean'
            ? SQL`${SessionAttendeeQ.session_breakout_id} IS ${sessionBreakoutId ? SQL`NOT` : SQL``} NULL AND`
            : SQL`${SessionAttendeeQ.session_breakout_id} = ${sessionBreakoutId} AND`
        }
        ${userId != null ? SQL`${SessionAttendeeQ.user_id} = ${userId} AND` : SQL``}
        ${roles != null ? SQL`${SessionAttendeeQ.role} = ANY(${roles}) AND` : SQL``}
        ${paranoid ? SQL`${SessionAttendeeQ.deleted_at} IS NULL AND` : SQL``}
        ${customWhere != null ? customWhere : SQL`TRUE`}
    ${queryGroupByToSQL(groupBy)}
    ${queryOrderToSQL(order)}
    ${queryLimitToSQL(limit)}
    `,

  getTotalTutorSpeakingPercentage: (): SQLFragment<number> =>
    SQL`(SUM(CASE WHEN ${SessionAttendeeQ.role} = ${SessionAttendeeRole.Tutor}
        THEN ${SessionAttendeeQ.speaking_duration_seconds} ELSE 0 END) * 100.0 /
        NULLIF(SUM(${SessionAttendeeQ.speaking_duration_seconds}), 0))`,

  hasEngagementData: (): SQLFragment<boolean> =>
    SQL`${SessionAttendeeQ.speaking_duration_seconds} IS NOT NULL`,

  hasRole: (role: SessionAttendeeRole): SQLFragment<boolean> =>
    SQL`${SessionAttendeeQ.role} = ${role}`,

  getZoomAttendanceSeconds: ({
    userId,
    sessionId,
    sessionBreakoutId,
    roles = [SessionAttendeeRole.Learner, SessionAttendeeRole.OnboardingBuddy],
  }: {
    userId: number | SQLFragment<number>
    sessionId: number | SQLFragment<number>
    sessionBreakoutId?: number | boolean | SQLFragment<number>
    roles?: SessionAttendeeRole[]
  }): SQLFragment<number> =>
    SQL`COALESCE(SUM(${SessionAttendeeQ.duration_seconds}), 0)
    ${SessionAttendeeQ.where({
      userId,
      sessionId,
      sessionBreakoutId,
      roles,
    })}`,

  getZoomChatCount: ({
    userId = null,
    sessionId,
    sessionBreakoutId,
    roles = [SessionAttendeeRole.Learner, SessionAttendeeRole.OnboardingBuddy],
  }: {
    userId?: number | SQLFragment<number> | null
    sessionId: number | SQLFragment<number>
    sessionBreakoutId?: number | boolean | SQLFragment<number>
    roles?: SessionAttendeeRole[]
  }): SQLFragment<number> =>
    SQL`COALESCE(SUM(COALESCE(${SessionAttendeeQ.count_chats}, 0)), 0)
    ${SessionAttendeeQ.where({
      userId,
      sessionId,
      sessionBreakoutId,
      roles,
    })}`,

  getZoomSpeakingDurationSeconds: ({
    userId = null,
    sessionId,
    sessionBreakoutId,
    roles = [SessionAttendeeRole.Learner, SessionAttendeeRole.OnboardingBuddy],
  }: {
    userId?: number | SQLFragment<number> | null
    sessionId: number | SQLFragment<number>
    sessionBreakoutId?: number | boolean | SQLFragment<number>
    roles?: SessionAttendeeRole[]
  }): SQLFragment<number> =>
    SQL`COALESCE(SUM(COALESCE(${SessionAttendeeQ.speaking_duration_seconds}, 0)), 0)
      ${SessionAttendeeQ.where({
        userId,
        sessionId,
        sessionBreakoutId,
        roles,
      })}`,

  // This query matches the Metabase question "SAT Bootcamp Learners"
  countUniqueSATBootcampUsers: ({
    type,
  }: {
    type: 'learner' | 'tutor' | 'all'
  }) => SQL`COUNT(DISTINCT ${SessionAttendeeQ.user_id})::int
      ${SessionAttendeeQ.where({
        include: [
          {
            association: 'Session',
            include: [
              {
                association: 'Series',
                include: [{ association: 'Course', where: CourseQ.isSATBootcampCourse() }],
              },
            ],
          },
        ],
        roles:
          type === 'all'
            ? [SessionAttendeeRole.Learner, SessionAttendeeRole.Tutor]
            : type === 'learner'
              ? [SessionAttendeeRole.Learner]
              : [SessionAttendeeRole.Tutor],
      })}`,

  didSpeak: (): SQLFragment<boolean> => SQL`${SessionAttendeeQ.speaking_duration_seconds} > 0`,

  didTypeInChat: (): SQLFragment<boolean> => SQL`${SessionAttendeeQ.count_chats} > 0`,

  getDetectedIssues: ({
    customWhere,
  }: {
    customWhere?: SQLFragment<boolean>
  }): SQLFragment<DetectedIssueAttributes> =>
    DetectedIssueQ.where(
      {
        userId: UserQ.id,
      },
      SQL`
        ${DetectedIssueQ.isForSession(SessionAttendeeQ.session_id)}
        ${customWhere != null ? SQL`AND ${customWhere}` : SQL``}
      `
    ),
}

export default SessionAttendeeQ
